// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LoadMediaData from "@bg/App/Handlers/MediaData/LoadMediaData";
import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import WidgetTitle from "@bgs/App/Components/WidgetTitle";
import { bgsCarouselStyles, BGSProductCarouselSlider } from "@bgs/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
    };
}

const MAX_SLIDES = 5;

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const mediaDataCollection = state.xMediaData.mediaDataCollection;
    return {
        mediaDataCollection,
        isLoading: state.xMediaData.isLoading,
        isAuthenticated: state.context.session.isAuthenticated,
        isHomePage: getCurrentPage(state).type.toLowerCase() === "homepage",
    };
};

const mapDispatchToProps = {
    LoadMediaData,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface InstagramStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    tunnel?: InjectableCss;
    caption?: TypographyPresentationProps;
    userNameWrapper?: InjectableCss;
    userNameText?: TypographyPresentationProps;
    media?: InjectableCss;
}

export const styles: InstagramStyles & BGSProductCarouselSlider = {
    ...bgsCarouselStyles,
    container: {
        css: css`
            .wrapper-element {
                @media (max-width: 481px) {
                    padding: 0 10px;
                    margin-bottom: 20px;
                }
            }
            .title-wrapper {
                @media (max-width: 481px) {
                    justify-content: flex-start;
                }
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            position: relative;
            @media (max-width: 481px) {
                flex-direction: column-reverse;
            }
        `,
    },
    tunnel: {
        css: css`
            width: 270px;
            height: 270px;
            padding-right: 40px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            background: ${getColor("common.ui3")};
            z-index: 1;
            &:before {
                content: " ";
                position: absolute;
                background: ${getColor("common.ui3")};
                left: -100%;
                width: 100%;
                z-index: 10000;
                height: 100%;
                z-index: 1;
            }
            @media (max-width: 481px) {
                width: 100%;
                padding: 20px 10px;
                height: auto;
                &&:before {
                    content: none;
                }
            }
        `,
    },
    caption: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.light,
        lineHeight: "20px",
        css: css`
            letter-spacing: 0;
        `,
    },

    userNameWrapper: {
        css: css`
            height: 36px;
            width: 160px;
            border: 2px solid ${getColor("text")};
            background-color: ${getColor("common.white")};
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 15px;
            padding: 11px 16px;
        `,
    },
    userNameText: {
        size: 12,
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.xSmall,
        css: css`
            letter-spacing: 0.83px;
        `,
    },

    media: {
        css: css`
            width: 100%;
            height: 270px;
            > img,
            video {
                width: 100%;
                height: 100%;
                object-fit: fill;
            }
        `,
    },
    mainContainer: {
        css: css`
            width: calc(100% - 270px);
            position: relative;
            @media (max-width: 900px) {
                width: calc(100% - 230px);
            }
            @media (max-width: 600px) {
                width: 100%;
            }
        `,
    },
    carouselWrapper: {
        css: css`
            &&:before {
                content: none;
            }
            @media (max-width: 1280px) {
                overflow: hidden;
                padding: 20px 0;
                &:before {
                    content: none;
                }
            }
            @media (max-width: 600px) {
                padding-left: 1px;
            }
        `,
    },
    slide: {
        css: css`
            min-width: 260px;
            @media (max-width: 1024px) {
                width: calc(100% / 3 - 10px);
                margin: 0 5px;
            }
            @media (max-width: 1000px) {
                width: calc(100% / 2 - 20px);
                margin: 0 10px;
            }
            @media (max-width: 600px) {
                width: calc(100% - 16px);
                margin: 0 8px;
            }
        `,
    },
    navigationWrapper: {
        css: css`
            ${bgsCarouselStyles.navigationWrapper?.css}
            right: 10px;
        `,
    },
};

const Instagram = ({
    fields,
    LoadMediaData,
    isLoading,
    mediaDataCollection,
    isHomePage,
    theme,
    shellContext,
}: Props) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    useEffect(() => {
        LoadMediaData({});
    }, []);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setSelectedIndex(embla ? embla.selectedScrollSnap() : 0);
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        if (typeof window === "undefined") {
            return 4;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, 10000);
    }, [windowResizeTime]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[3];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [mediaDataCollection, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !mediaDataCollection || mediaDataCollection.data.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable: false,
            loop: false,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, mediaDataCollection]);

    if (!mediaDataCollection || mediaDataCollection.data.length === 0) {
        return null;
    }

    if (!isHomePage) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <WidgetTitle title={fields.title} />
            <StyledWrapper
                css={css`
                    background: ${getColor("common.ui3")};
                `}
            >
                <Page
                    css={css`
                        padding-right: 0 !important;
                        @media (max-width: 768px) {
                            overflow: unset;
                        }
                        @media (max-width: 481px) {
                            padding: 0;
                        }
                    `}
                >
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.tunnel}>
                            <Typography {...styles.caption}>{fields.description}</Typography>
                            <Clickable
                                {...styles.userNameWrapper}
                                href={`https://www.instagram.com/${mediaDataCollection.data[0].userName}`}
                                target="_blank"
                            >
                                <Typography {...styles.userNameText}>
                                    @{mediaDataCollection.data[0].userName}
                                </Typography>
                            </Clickable>
                        </StyledWrapper>
                        <StyledWrapper {...styles.mainContainer}>
                            <StyledWrapper {...styles.navigationWrapper}>
                                {mediaDataCollection.data.length > slidesToScroll && (
                                    <Button
                                        {...styles.scrollPrev}
                                        disabled={!canScrollPrev}
                                        onClick={() => embla && embla.scrollPrev()}
                                        data-test-selector="prevBtn"
                                    >
                                        <ButtonIcon src={ChevronLeft} />
                                    </Button>
                                )}
                                <Typography
                                    css={css`
                                        font-weight: ${fontConfig.weight.semiBold};
                                        font-style: italic;
                                    `}
                                >{`${selectedIndex + 1} of ${Math.ceil(
                                    mediaDataCollection.data.length / slidesToScroll,
                                )}`}</Typography>
                                {mediaDataCollection.data.length > slidesToScroll && (
                                    <Button
                                        {...styles.scrollPrev}
                                        disabled={!canScrollNext}
                                        onClick={() => embla && embla.scrollNext()}
                                        data-test-selector="nextBtn"
                                    >
                                        <ButtonIcon src={ChevronRight} />
                                    </Button>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.carouselWrapper} ref={emblaRef}>
                                <StyledWrapper data-test-selector="slides" {...styles.innerContainer}>
                                    {mediaDataCollection.data.map(
                                        ({ id, mediaUrl, permaLink, caption, mediaType, userName }) => (
                                            <StyledWrapper key={id} {...styles.slide}>
                                                <Clickable
                                                    href={permaLink}
                                                    target="_blank"
                                                    {...styles.media}
                                                    title={caption}
                                                >
                                                    {mediaType.toLowerCase() === "video" ? (
                                                        <video autoPlay muted loop>
                                                            <source src={mediaUrl} />
                                                            {translate("Your Browser Does Not Support HTML 5 Video ")}
                                                        </video>
                                                    ) : (
                                                        <LazyImage
                                                            src={mediaUrl}
                                                            altText={caption}
                                                            css={css`
                                                                width: 100%;
                                                                img {
                                                                    height: 100%;
                                                                    object-fit: fill;
                                                                }
                                                            `}
                                                        />
                                                    )}
                                                </Clickable>
                                            </StyledWrapper>
                                        ),
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </Page>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Instagram Slider",
    fieldDefinitions: [
        {
            displayName: "Section Title",
            name: fields.title,
            editorTemplate: "TextField",
            defaultValue: "See Us In Action",
            fieldType: "Translatable",
        },
        {
            displayName: "Slider Description",
            name: fields.description,
            editorTemplate: "RichTextField",
            defaultValue:
                "Don't just take our word for it – let our clients show you how Bartle & Gibson is helping to shape up their space.",
            fieldType: "Translatable",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(Instagram))),
    definition,
};

export default widgetModule;
